import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getKpiBonusProgress } from '@/api/kpi'
import type { KpiRealizationItem } from '@/components/ui/charts/BonusPayoutChart'
import { pushNotification, useAppDispatch } from '@/store'
import {
  BonusProgressModel,
  BonusProgressResponseModel,
} from '@/types/dashboard'
import { areDateParamsValid } from '@/utils/filterParams'
import { isDateRangeWithinQuarter } from '@/utils/date'

const mapBonusProgress = (
  response: BonusProgressResponseModel
): BonusProgressModel => ({
  assignments: response.assignments.map((a) => ({
    id: a.id,
    kpiMetric: a.kpi_metric,
    kpiType: a.kpi_type,
    branchId: a.branch_id,
    branchName: a.branch_name,
    bonusAmountIf100Percent: a.bonus_amount_if_100_percent,
    achievementPercentage: a.achievement_percentage,
    achieved: a.achieved,
  })),
  summary: {
    currentBonusPayout: response.summary.current_bonus_payout,
    maxAchievableBonus: response.summary.max_achievable_bonus,
    maxEligibleBonusPercentage: response.summary.max_eligible_bonus_percentage,
  },
})

export default function useKpiBonusProgress(
  startDate: string,
  endDate: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const hasDateRange = useMemo(
    () =>
      areDateParamsValid(startDate) &&
      endDate != null &&
      String(endDate).trim() !== '',
    [startDate, endDate]
  )
  const isWithinQuarter = useMemo(
    () => isDateRangeWithinQuarter(startDate, endDate),
    [startDate, endDate]
  )
  const isReady = hasDateRange && isWithinQuarter
  const requiresQuarterRange = hasDateRange && !isWithinQuarter
  const loading = !isReady || isFetching

  const [bonusProgress, setBonusProgress] = useState<
    BonusProgressModel | undefined
  >()

  const fetchData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }

    setIsFetching(true)

    try {
      const response = await getKpiBonusProgress(startDate, endDate)
      setBonusProgress(mapBonusProgress(response))
    } catch (err) {
      const error = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )
      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: error.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, startDate, endDate, dispatch, t])

  const refetch = useCallback(() => fetchData(), [fetchData])

  const kpiRealizations: KpiRealizationItem[] = useMemo(() => {
    if (!bonusProgress?.assignments) return []
    return bonusProgress.assignments.map((a) => ({
      title: a.kpiType,
      value: Math.min(
        a.bonusAmountIf100Percent,
        (a.achievementPercentage / 100) * a.bonusAmountIf100Percent
      ),
      maxValue: a.bonusAmountIf100Percent,
    }))
  }, [bonusProgress])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    bonusProgress,
    kpiRealizations,
    loading,
    refetch,
    requiresQuarterRange,
  }
}
